import React, { ReactElement } from 'react';
import Text from '../../../../shared/uibuilder/form/input/Text';
import Row3x3x6x from '../../../../shared/web/uitheme/form/Row3x3x6x';
import Checkbox from '../../../../shared/uibuilder/form/input/Checkbox';
import RelativeRow, { EmptyCell } from '../../../../shared/web/uitheme/form/RelativeRow';
import Separator from '../../../../shared/web/uitheme/form/Separator';
import SmsRecipientsInput from './SmsRecipientsInput';
import { ValidationSchema } from '../../../../shared/uibuilder/validation';
import {
  PlanSmsSmartTagsContext
} from '../../../../shared/smarttags/SmsSmartTagsContext';
import {
  DueDateConfigInput, SmsPlanTaskSmartTagsWysiwyg,
  TasksGroupsDropdown
} from 'features/tasks-forms';
import { AfterSubmitFunc, FormTemplate, SubmitFormFunc } from 'ui-builder';
import { PlanTask, TeamPermission, ApiVersion } from 'nekst-api';
import { PlanPermission, useAuthorizationChecker } from 'authorization-scope';
import { IfWeekendDropdown } from 'features/nekst-widgets';
import { BusinessDaysToggleWrapper } from 'feature-toggle';
import DueDateRow from '../DueDateRow';

export interface BaseSmsTaskFormProps {
  title: string,
  submitFormFunc: SubmitFormFunc<PlanTask>,
  afterSubmitFunc?: AfterSubmitFunc,
  validationSchema: ValidationSchema,
  buttons?: ReactElement,
  initialData?: PlanTask
  planId: number
}

function CheckboxesRow() {

  const { isGranted } = useAuthorizationChecker();

  if (isGranted(TeamPermission.FEATURE_BUSINESS_DAYS)) {
    return (
      <RelativeRow weights={[2.5, 2, 3, 4.5]}>
        <Checkbox source="isKeyDate" label="Key Date" />
        <Checkbox source="isPrivate" label="Private" />
        <Checkbox source="useBusinessDaysCalculation" label="Business Days" />
        <Checkbox source="isAutoSent" label="Autosend" />
      </RelativeRow>
    );
  } else {
    return (
      <Row3x3x6x>
        <Checkbox source="isKeyDate" label="Key Date" />
        <Checkbox source="isPrivate" label="Private" />
        <Checkbox source="isDuringBusinessWeek" label="Only During Business Week" />
        <Checkbox source="isAutoSent" label="Autosend" />
      </Row3x3x6x>
    );
  }

}

function SmsTaskForm(props: BaseSmsTaskFormProps) {
  const { isGranted } = useAuthorizationChecker();

  return (
    <FormTemplate<PlanTask>
      submitFormFunc={props.submitFormFunc}
      afterSubmitFunc={props.afterSubmitFunc}
      validationSchema={props.validationSchema}
      title={props.title}
      buttons={props.buttons}
      initialData={{
        ...props.initialData || {},
        _version: isGranted(TeamPermission.FEATURE_BUSINESS_DAYS) ? ApiVersion.IF_WEEKEND_SHIFT : undefined
      }}
      isDisabled={!isGranted(PlanPermission.UPDATE)}
    >
      <Text source="name" label="Task Name" />
      <CheckboxesRow />
      <RelativeRow
        weights={[7, 1, 4]}
        rowClassName="form-row"
        isVisible={(data) => !data?.parentTaskRelation}
      >
        <DueDateConfigInput source="dueDateConfig" label="Due" />
        <EmptyCell />
        <BusinessDaysToggleWrapper>
          <IfWeekendDropdown />
        </BusinessDaysToggleWrapper>
      </RelativeRow>
      <Separator />
      <DueDateRow />
      <RelativeRow weights={[4, 6, 2]}>
        <TasksGroupsDropdown source="groupId" label="Group" planId={props.planId} />
      </RelativeRow>
      <Separator />
      <SmsPlanTaskSmartTagsWysiwyg source="description" label="SMS Text" planId={props.planId} />
      <Separator />
      <SmsRecipientsInput planId={props.planId} />
    </FormTemplate>
  );
}

export default function BaseSmsTaskForm(props: BaseSmsTaskFormProps) {
  return (
    <PlanSmsSmartTagsContext planId={props.planId}>
      <SmsTaskForm
        {...props}
      />
    </PlanSmsSmartTagsContext>
  );
}
